/**
 * Lunar 360 - Complete Animation Scripts (Vanilla JavaScript)
 * Contains all GSAP, scroll animations, and JavaScript effects
 * Compatible with Shopify and vanilla JavaScript environments
 *
 * DEPENDENCIES:
 * - GSAP: https://cdnjs.cloudflare.com/ajax/libs/gsap/3.12.2/gsap.min.js
 * - ScrollTrigger: https://cdnjs.cloudflare.com/ajax/libs/gsap/3.12.2/ScrollTrigger.min.js
 *
 * Include these script tags before this file:
 * <script src="https://cdnjs.cloudflare.com/ajax/libs/gsap/3.12.2/gsap.min.js"></script>
 * <script src="https://cdnjs.cloudflare.com/ajax/libs/gsap/3.12.2/ScrollTrigger.min.js"></script>
 */

// Declare global variables
var gsap
var ScrollTrigger
;(() => {
  // Check if GSAP is available
  if (typeof window.gsap === "undefined") {
    console.error("[Lunar360] GSAP is required. Please include GSAP script before this file.")
    return
  }

  // Assign global variables
  gsap = window.gsap
  ScrollTrigger = window.ScrollTrigger

  // Register GSAP plugins
  if (typeof ScrollTrigger !== "undefined") {
    gsap.registerPlugin(ScrollTrigger)
  }

  // ============================================================================
  // GSAP HORIZONTAL SCROLL HIGHLIGHTS
  // ============================================================================

  function HorizontalScrollHighlights(containerSelector, options) {
    this.container = document.querySelector(containerSelector)
    this.cardsContainer = null
    this.heading = null
    this.scrollTrigger = null
    this.isMounted = true
    this.isCleaningUp = false

    this.cards = (options && options.cards) || [
      {
        title: "Dedicated Sport Mode button gives instant access to your workouts with a single press.",
        video: "https://cdn.shopify.com/videos/c/o/v/8f3f9d61122547b49da7e80c72ebbd08.mp4",
      },
      {
        title: "Advanced health monitoring tracks your vitals 24/7 with precision sensors.",
        video: "https://cdn.shopify.com/videos/c/o/v/73b8fc3fe9d44ee39be83cb47ea0ad4b.mp4",
      },
      {
        title: "Rotate the crown to navigate through apps and menus with intuitive control.",
        video: "https://cdn.shopify.com/videos/c/o/v/5558d04be2b740478da109103fb33a37.mp4",
      },
    ]

    if (this.container) {
      this.init()
    }
  }

  HorizontalScrollHighlights.prototype.init = function () {
    this.cardsContainer = this.container.querySelector(".horizontal-cards-container")
    this.heading = this.container.querySelector(".horizontal-heading")

    if (!this.cardsContainer || !this.heading) {
      console.error("[Lunar360] Required elements not found for horizontal scroll")
      return
    }

    setTimeout(() => {
      if (!this.isMounted || this.isCleaningUp) return
      this.setupScrollTrigger()
    }, 200)
  }

  HorizontalScrollHighlights.prototype.setupScrollTrigger = function () {
    try {
      if (!this.container.isConnected || !this.cardsContainer.isConnected || !this.heading.isConnected) {
        console.log("[Lunar360] Elements not ready, skipping ScrollTrigger setup")
        return
      }

      var cardWidth = window.innerWidth >= 768 ? window.innerWidth * 0.8 : window.innerWidth
      var totalWidth = cardWidth * this.cards.length

      var scrollTween = gsap.to(this.cardsContainer, {
        x: -totalWidth + cardWidth,
        ease: "none",
        duration: 1,
      })

      if (this.isMounted && !this.isCleaningUp) {
        this.scrollTrigger = ScrollTrigger.create({
          trigger: this.container,
          start: "top top",
          end: "+=" + totalWidth,
          pin: true,
          scrub: 1,
          animation: scrollTween,
          anticipatePin: 1,
          onRefresh: () => {
            this.handleCleanup()
          },
          onUpdate: () => {
            this.handleCleanup()
          },
        })
      }
    } catch (error) {
      console.error("[Lunar360] ScrollTrigger creation failed:", error)
    }
  }

  HorizontalScrollHighlights.prototype.handleCleanup = function () {
    if (!this.isMounted || this.isCleaningUp) {
      try {
        if (this.scrollTrigger) {
          this.scrollTrigger.kill()
          this.scrollTrigger = null
        }
      } catch (e) {
        console.error("[Lunar360] Error during cleanup:", e)
      }
    }
  }

  HorizontalScrollHighlights.prototype.destroy = function () {
    this.isMounted = false
    this.isCleaningUp = true

    try {
      if (this.scrollTrigger) {
        this.scrollTrigger.kill()
        this.scrollTrigger = null
      }

      if (typeof ScrollTrigger !== "undefined") {
        ScrollTrigger.getAll().forEach((trigger) => {
          try {
            if (trigger.trigger === this.container) {
              trigger.kill()
            }
          } catch (e) {
            console.error("[Lunar360] Error killing ScrollTrigger:", e)
          }
        })

        ScrollTrigger.refresh()
      }
    } catch (error) {
      console.error("[Lunar360] Cleanup failed:", error)
    }
  }

  // ============================================================================
  // SCROLL IMAGE SEQUENCE ANIMATION
  // ============================================================================

  function ScrollImageSequence(containerSelector, options) {
    this.container = document.querySelector(containerSelector)
    this.canvas = null
    this.ctx = null
    this.currentFrame = 0
    this.targetFrame = 0
    this.images = []
    this.imagesLoaded = false
    this.loadingProgress = 0
    this.animationFrameId = null
    this.isMobile = window.innerWidth < 768
    this.scrollHandler = null
    this.resizeHandler = null

    var defaultOptions = {
      totalFrames: this.isMobile ? 126 : 211,
      scrollHeightMultiplier: this.isMobile ? 2 : 3,
      baseUrl: "https://cdn.shopify.com/s/files/1/0722/8106/3702/files/",
      mobilePrefix: "Mobile_",
      desktopPrefix: "Features_",
    }

    this.options = Object.assign(defaultOptions, options || {})

    if (this.container) {
      this.init()
    }
  }

  ScrollImageSequence.prototype.init = function () {
    this.setupCanvas()
    this.loadImages()
    this.setupScrollHandler()
    this.setupResizeHandler()
    this.startFrameAnimation()
  }

  ScrollImageSequence.prototype.setupCanvas = function () {
    this.canvas = this.container.querySelector("canvas") || document.createElement("canvas")
    this.ctx = this.canvas.getContext("2d")

    if (!this.container.querySelector("canvas")) {
      this.canvas.className = "scroll-sequence-canvas"
      this.container.appendChild(this.canvas)
    }

    this.setCanvasSize()
  }

  ScrollImageSequence.prototype.setCanvasSize = function () {
    var width = window.innerWidth
    var height = window.innerHeight

    this.canvas.width = width * window.devicePixelRatio
    this.canvas.height = height * window.devicePixelRatio
    this.ctx.scale(window.devicePixelRatio, window.devicePixelRatio)
    this.canvas.style.width = width + "px"
    this.canvas.style.height = height + "px"
  }

  ScrollImageSequence.prototype.loadImages = function () {
    console.log("[Lunar360] Starting to load", this.options.totalFrames, "images...")
    var self = this
    var loadedImages = []
    var loadedCount = 0

    function loadImage(index) {
      return new Promise((resolve, reject) => {
        var image = new Image()
        var frameNumber = index.toString().padStart(5, "0")
        var prefix = self.isMobile ? self.options.mobilePrefix : self.options.desktopPrefix
        var imagePath = self.options.baseUrl + prefix + frameNumber + ".jpg"

        image.onload = () => {
          resolve(image)
        }
        image.onerror = () => {
          reject(new Error("Failed to load: " + imagePath))
        }
        image.src = imagePath
      })
    }

    var loadPromises = []
    for (var i = 0; i < this.options.totalFrames; i++) {
      loadPromises.push(loadImage(i))
    }

    Promise.all(loadPromises)
      .then((images) => {
        console.log("[Lunar360] All images loaded successfully")
        self.images = images
        self.imagesLoaded = true
        self.hideLoading()
        self.drawFrame(0)
      })
      .catch((error) => {
        console.error("[Lunar360] Error loading images:", error)
      })
  }

  ScrollImageSequence.prototype.updateLoadingProgress = function () {
    var progressEl = this.container.querySelector(".loading-progress")
    if (progressEl) {
      progressEl.textContent = this.loadingProgress
    }
  }

  ScrollImageSequence.prototype.hideLoading = function () {
    var loadingEl = this.container.querySelector(".scroll-sequence-loading")
    if (loadingEl) {
      loadingEl.style.display = "none"
    }
  }

  ScrollImageSequence.prototype.startFrameAnimation = function () {
    var self = this
    function animateFrame() {
      var diff = self.targetFrame - self.currentFrame
      if (Math.abs(diff) < 0.1) {
        self.currentFrame = self.targetFrame
      } else {
        self.currentFrame += diff * 0.15
      }

      if (self.imagesLoaded) {
        self.drawFrame(Math.round(self.currentFrame))
      }

      self.animationFrameId = requestAnimationFrame(animateFrame)
    }

    this.animationFrameId = requestAnimationFrame(animateFrame)
  }

  ScrollImageSequence.prototype.drawFrame = function (frameIndex) {
    if (!this.imagesLoaded || !this.images[frameIndex]) return

    var img = this.images[Math.min(frameIndex, this.options.totalFrames - 1)]
    if (!img || !img.complete) return

    this.ctx.clearRect(0, 0, this.canvas.width / window.devicePixelRatio, this.canvas.height / window.devicePixelRatio)

    var canvasWidth = this.canvas.width / window.devicePixelRatio
    var canvasHeight = this.canvas.height / window.devicePixelRatio

    var canvasAspect = canvasWidth / canvasHeight
    var imgAspect = img.width / img.height

    var drawWidth, drawHeight, drawX, drawY

    if (imgAspect > canvasAspect) {
      drawHeight = canvasHeight
      drawWidth = canvasHeight * imgAspect
      drawX = (canvasWidth - drawWidth) / 2
      drawY = 0
    } else {
      drawWidth = canvasWidth
      drawHeight = canvasWidth / imgAspect
      drawX = 0
      drawY = (canvasHeight - drawHeight) / 2
    }

    this.ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight)
  }

  ScrollImageSequence.prototype.setupScrollHandler = function () {
    var self = this
    var lastFrameIndex = -1
    var ticking = false

    function handleScroll() {
      if (ticking || !self.imagesLoaded) return

      ticking = true
      requestAnimationFrame(() => {
        var rect = self.container.getBoundingClientRect()
        var containerHeight = self.container.offsetHeight
        var viewportHeight = window.innerHeight

        var scrollableDistance = containerHeight - viewportHeight
        var scrolled = Math.max(0, -rect.top)
        var scrollProgress = Math.max(0, Math.min(1, scrolled / scrollableDistance))

        var frameIndex = scrollProgress * (self.options.totalFrames - 1)

        if (Math.abs(frameIndex - lastFrameIndex) > 0.1) {
          self.targetFrame = frameIndex
          lastFrameIndex = frameIndex
        }

        ticking = false
      })
    }

    window.addEventListener("scroll", handleScroll, { passive: true })
    this.scrollHandler = handleScroll
  }

  ScrollImageSequence.prototype.setupResizeHandler = function () {
    var self = this
    function handleResize() {
      var wasMobile = self.isMobile
      self.isMobile = window.innerWidth < 768

      self.setCanvasSize()

      if (wasMobile !== self.isMobile) {
        self.options.totalFrames = self.isMobile ? 126 : 211
        self.imagesLoaded = false
        self.loadImages()
      } else if (self.imagesLoaded && self.currentFrame >= 0) {
        self.drawFrame(Math.round(self.currentFrame))
      }
    }

    window.addEventListener("resize", handleResize)
    this.resizeHandler = handleResize
  }

  ScrollImageSequence.prototype.destroy = function () {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId)
    }

    if (this.scrollHandler) {
      window.removeEventListener("scroll", this.scrollHandler)
    }

    if (this.resizeHandler) {
      window.removeEventListener("resize", this.resizeHandler)
    }
  }

  // ============================================================================
  // SMOOTH SCROLL ENHANCER
  // ============================================================================

  function SmoothScrollEnhancer(options) {
    this.currentScrollY = 0
    this.targetScrollY = 0
    this.isScrolling = false
    this.animationId = null
    this.wheelHandler = null
    this.keyHandler = null

    var defaultOptions = {
      lerpFactor: 0.08,
      scrollSpeed: 1.2,
      keyScrollAmount: 0.8,
    }

    this.options = Object.assign(defaultOptions, options || {})

    this.init()
  }

  SmoothScrollEnhancer.prototype.init = function () {
    if (typeof window === "undefined") return

    this.currentScrollY = window.scrollY
    this.targetScrollY = window.scrollY
    this.setupEventListeners()
  }

  SmoothScrollEnhancer.prototype.lerp = (start, end, factor) => start + (end - start) * factor

  SmoothScrollEnhancer.prototype.smoothScroll = function () {
    this.currentScrollY = this.lerp(this.currentScrollY, this.targetScrollY, this.options.lerpFactor)

    if (Math.abs(this.targetScrollY - this.currentScrollY) > 0.1) {
      window.scrollTo(0, this.currentScrollY)
      this.animationId = requestAnimationFrame(() => {
        this.smoothScroll()
      })
    } else {
      window.scrollTo(0, this.targetScrollY)
      this.isScrolling = false
    }
  }

  SmoothScrollEnhancer.prototype.handleWheel = function (e) {
    e.preventDefault()

    var delta = e.deltaY
    this.targetScrollY += delta * this.options.scrollSpeed

    var maxScroll = document.documentElement.scrollHeight - window.innerHeight
    this.targetScrollY = Math.max(0, Math.min(this.targetScrollY, maxScroll))

    if (!this.isScrolling) {
      this.isScrolling = true
      this.currentScrollY = window.scrollY
      this.smoothScroll()
    }
  }

  SmoothScrollEnhancer.prototype.handleKeydown = function (e) {
    var scrollAmount = window.innerHeight * this.options.keyScrollAmount

    switch (e.key) {
      case "ArrowDown":
      case "PageDown":
        e.preventDefault()
        this.targetScrollY = Math.min(
          this.targetScrollY + scrollAmount,
          document.documentElement.scrollHeight - window.innerHeight,
        )
        break
      case "ArrowUp":
      case "PageUp":
        e.preventDefault()
        this.targetScrollY = Math.max(this.targetScrollY - scrollAmount, 0)
        break
      case "Home":
        e.preventDefault()
        this.targetScrollY = 0
        break
      case "End":
        e.preventDefault()
        this.targetScrollY = document.documentElement.scrollHeight - window.innerHeight
        break
      default:
        return
    }

    if (!this.isScrolling) {
      this.isScrolling = true
      this.currentScrollY = window.scrollY
      this.smoothScroll()
    }
  }

  SmoothScrollEnhancer.prototype.setupEventListeners = function () {
    if (typeof window === "undefined") return

    this.wheelHandler = (e) => {
      this.handleWheel(e)
    }
    this.keyHandler = (e) => {
      this.handleKeydown(e)
    }

    window.addEventListener("wheel", this.wheelHandler, { passive: false })
    window.addEventListener("keydown", this.keyHandler)
  }

  SmoothScrollEnhancer.prototype.destroy = function () {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId)
    }

    if (this.wheelHandler) {
      window.removeEventListener("wheel", this.wheelHandler)
    }

    if (this.keyHandler) {
      window.removeEventListener("keydown", this.keyHandler)
    }
  }

  // ============================================================================
  // INTERSECTION OBSERVER ANIMATIONS
  // ============================================================================

  function IntersectionAnimations() {
    this.observers = []
    this.init()
  }

  IntersectionAnimations.prototype.init = function () {
    if (typeof window === "undefined" || typeof window.IntersectionObserver === "undefined") return

    this.setupSectionAnimations()
    this.setupCardGridAnimations()
    this.setupColorSwatchAnimations()
  }

  IntersectionAnimations.prototype.createObserver = (callback, options) => {
    var defaultOptions = {
      threshold: 0.3,
      rootMargin: "50px",
    }

    var finalOptions = Object.assign(defaultOptions, options || {})
    return new window.IntersectionObserver(callback, finalOptions)
  }

  IntersectionAnimations.prototype.setupSectionAnimations = function () {
    // Section 5 animations
    var section5 = document.querySelector('[data-section="section-5"]')
    if (section5) {
      var observer5 = this.createObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            section5.classList.add("section-visible")
            this.animateFeatureBoxes(section5)
          }
        })
      })

      observer5.observe(section5)
      this.observers.push(observer5)
    }

    // Section 6 animations
    var section6 = document.querySelector('[data-section="section-6"]')
    if (section6) {
      var observer6 = this.createObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            section6.classList.add("section-visible")
            this.animateImageBoxes(section6)
          }
        })
      })

      observer6.observe(section6)
      this.observers.push(observer6)
    }

    // Section 7 animations
    var section7 = document.querySelector('[data-section="section-7"]')
    if (section7) {
      var observer7 = this.createObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            section7.classList.add("section-visible")
          }
        })
      })

      observer7.observe(section7)
      this.observers.push(observer7)
    }
  }

  IntersectionAnimations.prototype.animateFeatureBoxes = (section) => {
    var boxes = section.querySelectorAll(".feature-box")
    boxes.forEach((box, index) => {
      setTimeout(
        () => {
          box.classList.add("animate-in")
        },
        index * 200 + 400,
      )
    })
  }

  IntersectionAnimations.prototype.animateImageBoxes = (section) => {
    var boxes = section.querySelectorAll(".image-box")
    boxes.forEach((box, index) => {
      setTimeout(
        () => {
          box.classList.add("animate-in")
        },
        index * 200 + 200,
      )
    })
  }

  IntersectionAnimations.prototype.setupCardGridAnimations = function () {
    var cardGrid = document.querySelector(".animated-card-grid")
    if (!cardGrid) return

    var cards = cardGrid.querySelectorAll(".grid-card")

    cards.forEach((card, index) => {
      var observer = this.createObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setTimeout(() => {
                card.classList.add("card-visible")
              }, index * 150)
            }
          })
        },
        { threshold: 0.2 },
      )

      observer.observe(card)
      this.observers.push(observer)
    })
  }

  IntersectionAnimations.prototype.setupColorSwatchAnimations = function () {
    var colorSection = document.querySelector(".color-swatch-section")
    if (!colorSection) return

    var observer = this.createObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          colorSection.classList.add("section-visible")
          this.animateColorElements(colorSection)
        }
      })
    })

    observer.observe(colorSection)
    this.observers.push(observer)
  }

  IntersectionAnimations.prototype.animateColorElements = (section) => {
    var elements = section.querySelectorAll(".color-element")
    elements.forEach((element, index) => {
      setTimeout(
        () => {
          element.classList.add("animate-in")
        },
        index * 200 + 200,
      )
    })
  }

  IntersectionAnimations.prototype.destroy = function () {
    this.observers.forEach((observer) => {
      observer.disconnect()
    })
    this.observers = []
  }

  // ============================================================================
  // COLOR VARIANTS SLIDER
  // ============================================================================

  function ColorVariantsSlider(containerSelector, options) {
    this.container = document.querySelector(containerSelector)
    this.currentIndex = 0
    this.intervalId = null

    var defaultOptions = {
      autoAdvanceInterval: 4000,
      transitionDuration: 700,
      images: [
        "https://cdn.shopify.com/s/files/1/0722/8106/3702/files/Beauty-fold.webp?v=1756300932",
        "https://cdn.shopify.com/s/files/1/0722/8106/3702/files/Beauty-fold--2.webp?v=1756300932",
        "https://cdn.shopify.com/s/files/1/0722/8106/3702/files/Beauty-fold--3.webp?v=1756300932",
        "https://cdn.shopify.com/s/files/1/0722/8106/3702/files/Beauty-fold-4.webp?v=1756300932",
      ],
    }

    this.options = Object.assign(defaultOptions, options || {})

    if (this.container) {
      this.init()
    }
  }

  ColorVariantsSlider.prototype.init = function () {
    this.setupSlider()
    this.setupControls()
    this.startAutoAdvance()
  }

  ColorVariantsSlider.prototype.setupSlider = function () {
    this.updateSlider()
    this.updateDots()
  }

  ColorVariantsSlider.prototype.setupControls = function () {
    var prevBtn = this.container.querySelector(".slider-prev")
    var nextBtn = this.container.querySelector(".slider-next")
    var dots = this.container.querySelectorAll(".slider-dot")

    if (prevBtn) {
      prevBtn.addEventListener("click", () => {
        this.goToPrevious()
      })
    }

    if (nextBtn) {
      nextBtn.addEventListener("click", () => {
        this.goToNext()
      })
    }

    dots.forEach((dot, index) => {
      dot.addEventListener("click", () => {
        this.goToSlide(index)
      })
    })
  }

  ColorVariantsSlider.prototype.goToSlide = function (index) {
    this.currentIndex = index
    this.updateSlider()
    this.updateDots()
    this.restartAutoAdvance()
  }

  ColorVariantsSlider.prototype.goToNext = function () {
    this.currentIndex = (this.currentIndex + 1) % this.options.images.length
    this.updateSlider()
    this.updateDots()
  }

  ColorVariantsSlider.prototype.goToPrevious = function () {
    this.currentIndex = this.currentIndex === 0 ? this.options.images.length - 1 : this.currentIndex - 1
    this.updateSlider()
    this.updateDots()
  }

  ColorVariantsSlider.prototype.updateSlider = function () {
    var sliderTrack = this.container.querySelector(".slider-track")
    if (sliderTrack) {
      sliderTrack.style.transform = "translateX(-" + this.currentIndex * 100 + "%)"
    }
  }

  ColorVariantsSlider.prototype.updateDots = function () {
    var dots = this.container.querySelectorAll(".slider-dot")
    var currentIndex = this.currentIndex
    dots.forEach((dot, index) => {
      if (index === currentIndex) {
        dot.classList.add("active")
      } else {
        dot.classList.remove("active")
      }
    })
  }

  ColorVariantsSlider.prototype.startAutoAdvance = function () {
    this.intervalId = setInterval(() => {
      this.goToNext()
    }, this.options.autoAdvanceInterval)
  }

  ColorVariantsSlider.prototype.restartAutoAdvance = function () {
    if (this.intervalId) {
      clearInterval(this.intervalId)
    }
    this.startAutoAdvance()
  }

  ColorVariantsSlider.prototype.destroy = function () {
    if (this.intervalId) {
      clearInterval(this.intervalId)
    }
  }

  // ============================================================================
  // MAIN INITIALIZATION
  // ============================================================================

  function Lunar360Animations() {
    this.components = {}
    this.init()
  }

  Lunar360Animations.prototype.init = function () {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () => {
        this.initializeComponents()
      })
    } else {
      this.initializeComponents()
    }
  }

  Lunar360Animations.prototype.initializeComponents = function () {
    console.log("[Lunar360] Initializing animations...")

    // Initialize smooth scroll enhancer
    this.components.smoothScroll = new SmoothScrollEnhancer()

    // Initialize horizontal scroll highlights
    var horizontalContainer = document.querySelector(".horizontal-scroll-container")
    if (horizontalContainer) {
      this.components.horizontalScroll = new HorizontalScrollHighlights(".horizontal-scroll-container")
    }

    // Initialize scroll image sequence
    var sequenceContainer = document.querySelector(".scroll-sequence-container")
    if (sequenceContainer) {
      this.components.scrollSequence = new ScrollImageSequence(".scroll-sequence-container")
    }

    // Initialize intersection animations
    this.components.intersectionAnimations = new IntersectionAnimations()

    // Initialize color variants slider
    var sliderContainer = document.querySelector(".color-variants-slider")
    if (sliderContainer) {
      this.components.colorSlider = new ColorVariantsSlider(".color-variants-slider")
    }

    console.log("[Lunar360] All animations initialized")
  }

  Lunar360Animations.prototype.destroy = function () {
    Object.keys(this.components).forEach((key) => {
      var component = this.components[key]
      if (component && typeof component.destroy === "function") {
        component.destroy()
      }
    })
    this.components = {}
  }

  // ============================================================================
  // EXPORT FOR GLOBAL USE
  // ============================================================================

  // Initialize automatically
  window.Lunar360 = new Lunar360Animations()

  // Export classes for manual initialization
  window.Lunar360Classes = {
    HorizontalScrollHighlights: HorizontalScrollHighlights,
    ScrollImageSequence: ScrollImageSequence,
    SmoothScrollEnhancer: SmoothScrollEnhancer,
    IntersectionAnimations: IntersectionAnimations,
    ColorVariantsSlider: ColorVariantsSlider,
    Lunar360Animations: Lunar360Animations,
  }

  console.log("[Lunar360] Animation system loaded and ready")
})()
